import { sectionRenderer } from '@theme/section-renderer';

class CeryClearCart extends HTMLElement {
  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-cery-clear-cart-modal]');
    this.addEventListener('click', this.#handleClick);
    this.#dialog?.addEventListener('click', this.#handleDialogClick);
    this.#dialog?.addEventListener('cancel', this.#handleDialogCancel);
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#handleDialogCancel);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    this.#closeDialog(false);
  }

  #handleClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const openButton = event.target.closest('[data-cery-clear-cart-open]');

    if (openButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#openDialog();
    }
  };

  #handleDialogClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest('[data-cery-clear-cart-close]');
    const confirmButton = event.target.closest('[data-cery-clear-cart-confirm]');

    if (event.target === this.#dialog && this.#isBackdropClick(event)) {
      event.preventDefault();
      this.#closeDialog();
      return;
    }

    if (closeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeDialog();
      return;
    }

    if (!(confirmButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    await this.#clearCart(confirmButton);
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.#closeDialog();
  };

  #handleDialogClose = () => {
    this.#dialog?.setAttribute('aria-hidden', 'true');
    this.#lastFocusedElement?.focus();
  };

  #openDialog() {
    const dialog = this.#dialog;
    if (!dialog) return;

    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.setAttribute('aria-hidden', 'false');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    requestAnimationFrame(() => {
      const closeButton = this.querySelector('[data-cery-clear-cart-close]');
      if (closeButton instanceof HTMLElement) closeButton.focus();
    });
  }

  /**
   * @param {boolean} [restoreFocus]
   */
  #closeDialog(restoreFocus = true) {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.setAttribute('aria-hidden', 'true');
      if (restoreFocus) this.#lastFocusedElement?.focus();
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #clearCart(button) {
    const buttons = this.querySelectorAll('button');
    buttons.forEach((control) => {
      control.disabled = true;
    });
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${Theme.routes.cart_url.replace(/\/$/, '')}/clear.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Cart clear failed');

      const cart = await response.json();
      this.#closeDialog(false);
      this.#updateCartIndicators(cart);
      const sectionModes = new Map();

      for (const component of document.querySelectorAll('cart-items-component')) {
        if (!(component instanceof HTMLElement) || !component.dataset.sectionId) continue;

        const currentMode = sectionModes.get(component.dataset.sectionId);
        if (component.hasAttribute('data-drawer') || currentMode === 'hydration') {
          sectionModes.set(component.dataset.sectionId, 'hydration');
        } else {
          sectionModes.set(component.dataset.sectionId, 'full');
        }
      }

      await Promise.all(
        Array.from(sectionModes.entries()).map(([sectionId, mode]) =>
          sectionRenderer.renderSection(sectionId, { cache: false, mode })
        )
      );
    } catch (error) {
      console.error(error);
      window.location.href = Theme.routes.cart_url;
    } finally {
      buttons.forEach((control) => {
        control.disabled = false;
      });
      button.removeAttribute('aria-busy');
    }
  }

  #isDialogHidden() {
    return !this.#dialog || !this.#dialog.open;
  }

  /**
   * @param {{ item_count?: number }} cart
   */
  #updateCartIndicators(cart) {
    const itemCount = Number(cart?.item_count) || 0;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    for (const cartIcon of document.querySelectorAll('cart-icon')) {
      if ('renderCartBubble' in cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        cartIcon.renderCartBubble(itemCount, false, false);
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #isBackdropClick(event) {
    const dialog = this.#dialog;
    if (!dialog) return false;

    const rect = dialog.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }
}

if (!customElements.get('cery-clear-cart')) {
  customElements.define('cery-clear-cart', CeryClearCart);
}
